// REQ-102 + REQ-103 + REQ-104 + REQ-106 — Pantalla "Reconocer" (/app/reconocer). Es el "home" del
// Programa: el panel de envío arriba y el muro de post-its (PostItWall) con el feed real debajo.
// El envío refresca el muro automáticamente (invalidación de queries).
//
// REQ-104 — Coexistencia clásico/interactivo por configuración: un selector de modo permite elegir
// entre el formulario CLÁSICO (ReconocerPanel, calcado 1:1 del demo — `.rp-single`) y el POST-IT
// INTERACTIVO / Word Builder (WordBuilderPanel, reimplementado sobre `.ri-*`, ver H-45). Ambos
// disponibles por defecto; la config de OU/campaña puede restringir a uno.
//
// FASE 4 (paridad demo) — el selector Clásico/Interactivo (`.rec-mode-*`) y el botón de carga
// masiva NO existen en el demo (el demo solo tiene el formulario clásico con la carga masiva
// embebida, ver ReconocerPanel). Es funcionalidad real solo del integrado: se RESTILA al lenguaje
// visual del demo en vez de retirarse (decisión D-F4-1, ver docs/paridad/MAPA-CLASES.md /
// ESTADO.md). REQ-106 — Botón de carga masiva CSV ahora vive EMBEBIDO dentro de ReconocerPanel
// (H-42); el modal (BulkUploadModal) sigue siendo el destino de "Subir CSV"/"Descargar plantilla".
import { useRef, useMemo, useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { FundamentalDto, PersonSummary } from '@/api/recognition';
import { getFundamentalColor, getFundamentalName, pickLocalized, useRecognitionCatalog, useLandingBanner, useRecognitionRules } from '@/api/recognition';
import { fundamentalsLogoSrc } from '@/views/FundamentalsView/fundamentalsLogo';
import { fundamentalsStackedLogoSrc } from './fundamentalsStackedLogo';
import { useUserContext } from '@/contexts/UserContext';
import { useRecognitionPermissions } from './access/useRecognitionPermissions';
import { PageSpinner } from '@/components/common';
import { RecognitionShell } from './components';
import { RECOGNITION_NAV_LINKS } from './recognitionNavLinks';
import { useRecognitionEntryGate } from './useCampaignNavEntry';
import { ReconocerPanel } from './ReconocerPanel';
import { WordBuilderPanel } from './WordBuilderPanel';
import { BulkUploadModal } from './bulkUpload/BulkUploadModal';
import { PostItWall } from './PostItWall';
import { StreakWeekTracker } from './StreakTracker';
import { getFundamentalIcon } from './fundamentalAssets';
import { fundamentalesOfrecibles } from './postitShared';

type Mode = 'classic' | 'interactive';

// D3 (paridad demo) — el rail de la izquierda. El demo (`HomePage.tsx` : 320-349) pinta un TILE
// de 80px con el color del Fundamental de fondo, icono real de 48px y borde derecho redondeado
// (`0 45px 45px 0`); al hacer hover se expande y revela nombre + descripción. Antes aquí había
// una pastilla blanca con un punto de color, que no se parecía en nada. El demo usa framer-motion
// para la expansión; este repo no lo tiene como dependencia, así que la animación de ancho vive
// en CSS (`.fund-pill-wrapper`, global.css) — el resultado visual es el mismo.
function FundamentalPill({ fund, index, lang }: Readonly<{ fund: FundamentalDto; index: number; lang: string }>) {
  const name = getFundamentalName(fund, lang);
  const desc = pickLocalized(fund.descriptions, lang) || fund.description || '';
  return (
    <div
      className="fund-pill-wrapper"
      style={({ '--pill-color': getFundamentalColor(fund, index) } as CSSProperties)}
    >
      <div className="fund-pill">
        <div className="fund-pill-content">
          <h4 className="fund-pill-name">{name}</h4>
          {desc && <p className="fund-pill-desc">{desc}</p>}
        </div>
        <div className="fund-pill-icon-area">
          <img src={getFundamentalIcon(fund, index)} alt={name} className="fund-pill-icon" />
        </div>
      </div>
    </div>
  );
}

function ReconocerContent() {
  const { t, i18n } = useTranslation('recognition');
  const wallRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const { demoUser } = useUserContext();
  const catalog = useRecognitionCatalog();
  // WP-07.3 — el banner del hero (título 1/2, subtítulo, logo) lo configura el admin en Campañas →
  // Fundamentales. Se lee por GET no-admin (useLandingBanner) y cae a los literales i18n / SVG estáticos
  // cuando no hay config o el endpoint falla (retry:0, sin lanzar). Fallback por-campo (no global).
  const lang = i18n.language;
  const banner = useLandingBanner(lang).data;
  const heroTitle1 = pickLocalized(banner?.bannerTitleLine1, lang) || t('home.titleLine1');
  const heroTitle2 = pickLocalized(banner?.bannerTitleLine2, lang) || t('home.titleVive');
  const heroSubtitle = pickLocalized(banner?.bannerSubtitle, lang) || t('home.subtitle');
  // QA (2026-08-31) — logo: lectura EXACTA del idioma activo, NO `pickLocalized` (que cae en cascada
  // idioma → es → en → primera clave, correcta para texto pero no para un logo: mostraría el logo de
  // OTRO idioma en vez de caer al asset local por-idioma). Mismo fix ya aplicado en `BrillamosView.tsx`
  // y `PerfilView.tsx` para este mismo campo. Sin logo propio del idioma activo, los 3 usos de
  // `heroLogo` más abajo ya caen a su propio fallback (`/fundamentals-v2.svg` o `fundamentalsLogoSrc`).
  const heroLogo = banner?.bannerLogoUrl?.[lang.split('-')[0].toLowerCase()];

  const [mode, setMode] = useState<Mode>('classic');
  const [showBulk, setShowBulk] = useState(false);

  // REQ-104 (WP toggles gestión reconocimiento) — el admin puede fijar la modalidad de envío
  // (`entryPointMode`, GestionSettingsDto): 'A' = solo clásico, 'B' = solo interactivo, 'both' =
  // el empleado elige (comportamiento previo). Con 'A'/'B' fijado, el selector visual se oculta
  // (más abajo) y el estado interno `mode` se fuerza a coincidir, para que el panel realmente
  // montado sea siempre el permitido y no solo el selector.
  const rules = useRecognitionRules();
  const entryPointMode = rules.data?.entryPointMode ?? 'both';

  // Bug #21, detalle adicional — el rail de la izquierda pintaba TODO `catalog.data` sin filtrar,
  // así que un Fundamental deshabilitado (Gestión) o eliminado (`isActive=false`, catálogo de
  // Pulso) seguía apareciendo ahí aunque ya no estuviera entre las tarjetas seleccionables de
  // `ReconocerPanel` (que sí aplica este mismo filtro, ver `fundamentalesOfrecibles`).
  const railFunds = useMemo(
    () => fundamentalesOfrecibles(catalog.data, rules.data?.fundamentalesDeshabilitados),
    [catalog.data, rules.data?.fundamentalesDeshabilitados],
  );

  useEffect(() => {
    if (entryPointMode === 'A' && mode !== 'classic') setMode('classic');
    if (entryPointMode === 'B' && mode !== 'interactive') setMode('interactive');
  }, [entryPointMode, mode]);

  // REQ-106 — la carga masiva se OCULTA para OUs sin permiso: la OU con flag GDPR no la ve, y solo
  // los roles que administran el programa. El backend revalida el permiso real (bulkCsv).
  // D-5: el predicado vive en un único sitio — ver `access/useRecognitionPermissions.ts`.
  const { canBulk } = useRecognitionPermissions();

  // REQ-120 — prefill "Reconocer a X" desde el perfil de un compañero (?receptor=&receptorName=).
  const initialRecipients = useMemo<PersonSummary[] | undefined>(() => {
    const id = searchParams.get('receptor');
    if (!id) return undefined;
    const name = searchParams.get('receptorName');
    return [{ id, email: name ?? id, displayName: name }];
  }, [searchParams]);

  const scrollToWall = () => wallRef.current?.scrollIntoView({ behavior: 'smooth' });
  const scrollToForm = () => document.getElementById('reconocer-section')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <>
      {/* D4b — NO volver a abrir `.home-page-root > .home-dashboard-area > .dashboard-root`:
          `RecognitionShell` (components.tsx : 35-42) ya emite ese contenedor. Al duplicarlo, el
          `margin-left: 300px` y el `max-width` del dashboard se aplicaban DOS veces y la tarjeta
          hero quedaba en 214px de ancho — el aplastamiento visible frente al demo. El rail es
          `position: fixed`, así que su sitio en el DOM no altera el flujo. */}
      <div className="fund-pills-rail" aria-label={t('catalog.title')}>
        {railFunds.map((fund, index) => (
          <FundamentalPill key={fund.id} fund={fund} index={index} lang={i18n.language} />
        ))}
      </div>
      <div className="dashboard-row dashboard-row--top">
        {/* 2026-11-02 — con logo del admin la tarjeta lleva `--logo-propio`: sin la mascota que asoma (se
            encimaba con el logo y el subtítulo), igual que la vista previa del banner. */}
        <section className={heroLogo ? 'dash-card dash-card--hero dash-card--logo-propio' : 'dash-card dash-card--hero'}>
          <img src="/persona-asomada.svg" className="hero-peek-mascot" alt="" />
          {/* D1/D2 (paridad demo) — título con el logo Fundamentals, calcado de `HomePage.tsx` :
              476-484 del demo. Antes se usaba `RecognitionHeader`, que pinta un `<h1>` plano de
              una sola línea: el logo (`/fundamentals-v2.svg`, ya presente en `public/`) no se
              renderizaba nunca y el copy era el genérico de formulario.
              Sin `<br/>` forzado entre `titleLine1`/`titleVive`: reportado en vivo que en el
              rango de una columna (ver logo ancho más abajo) sobraba ancho de sobra para que
              "Nomina a quien vive los" entre en una sola línea; el salto duro lo impedía siempre,
              sin importar el ancho disponible. Sin el `<br/>`, el wrap es responsivo normal —
              en anchos angostos (Teams/móvil) el texto simplemente envuelve solo. */}
          {/* Sin `<header>` intermedio: el demo cuelga `h1`, `p` y el botón como hermanos
              directos de `.dash-hero-content`. El wrapper añadía el `margin-bottom: 1rem` de
              `.rec-header` entre el subtítulo y el CTA, que el demo no tiene. */}
          <div className="dash-hero-content">
            {/* `.dash-hero-mascot-sm` (persona-torso, mismo asset e igual patrón que
                `.rp-header-mascot` en ReconocerPanel.tsx : 275) solo se pinta en pantallas
                reducidas (ver @media en recognition-shell.css) — ahí sustituye al wordmark
                `fundamentals-v2.svg` y a `.hero-peek-mascot`, que a ese ancho quedaban
                encimados sobre el título (reportado en vivo: se ve muy apretado en Teams). */}
            <div className="dash-hero-title-row">
              <img src="/persona-torso.svg" alt="" className="dash-hero-mascot-sm" aria-hidden="true" />
              <h1 className="dash-hero-title">
                {/* T-02 — línea 1 y línea 2 en renglones separados (el admin las captura como
                    "Título línea 1 / línea 2"). Son BLOQUES, no un `<br/>` forzado: apilan siempre
                    pero cada línea envuelve sola en anchos angostos, sin salto duro a media frase. */}
                <span className="dash-hero-line1">{heroTitle1}</span>
                {heroTitle2 && <span className="dash-hero-normal dash-hero-line2">{heroTitle2}</span>}
                {/* 2026-11-02 — Bug de QA: con un logo SUBIDO por el administrador (Campañas ›
                    Fundamentales › «Diseño del banner»), ese PNG se pintaba en los slots del wordmark de
                    Fundamentales (`.dash-hero-logo` / `-wide`, a todo lo ancho, hasta 560 px): un logo casi
                    cuadrado medía ~420 px de alto y empujaba el subtítulo y el botón fuera de la vista. Ahora
                    el logo del admin va en UN solo slot acotado (`.dash-hero-logo-custom`), como en la vista
                    previa del banner; sin logo propio, los tres slots de siempre. */}
                {heroLogo ? (
                  <img src={heroLogo} alt={t('shell.fundamentals')} className="dash-hero-logo-custom" />
                ) : (
                  <>
                    {/* QA (2026-09-01) — DT-029: el fallback `/fundamentals-v2.svg` es "FUNDAMENTALS" en
                        INGLÉS dibujado a mano, sin variante por idioma — mismo defecto que ya se corrigió
                        en `postitShared.tsx` para esta misma composición apilada. `fundamentalsStackedLogoSrc`
                        cae al arte apilado nuevo en español y al wordmark ancho en los demás idiomas. */}
                    <img src={fundamentalsStackedLogoSrc(i18n.language)} alt="Fundamentals" className="dash-hero-logo" />
                    {/* Wordmark ancho (mismo asset e idiomas que FundamentalsView, ver
                        `fundamentalsLogoSrc`) — sustituye a `.dash-hero-logo` (apilado, más alto que
                        ancho) en el rango donde el hero pasa a ocupar la fila completa y los post-its
                        bajan debajo (ver `@media (max-width: 1536px)` en recognition-shell.css):
                        reportado en vivo que ahí el logo apilado + la mascota grande se veían
                        amontonados. El banner horizontal ocupa mucha menos altura para el mismo ancho,
                        así que deja más aire para la mascota. */}
                    <img src={fundamentalsLogoSrc(i18n.language)} alt={t('shell.fundamentals')} className="dash-hero-logo-wide" />
                    {/* WP-07.3 — en ≤1024px (Teams/móvil) los dos <img> de arriba se ocultan y queda el texto. */}
                    <span className="dash-hero-logo-text">{t('shell.fundamentals')}</span>
                  </>
                )}
              </h1>
            </div>
            <p className="dash-hero-subtitle">{heroSubtitle}</p>
            <button type="button" className="dash-hero-cta" onClick={scrollToForm}>
              {t('home.cta')}
            </button>
          </div>
        </section>
        {/* Los dos muros van APILADOS dentro de la misma tarjeta, como el demo
            (`HomePage.tsx` : 487-552): cabecera ✉️ Enviados + rejilla, y debajo ⭐ Recibidos +
            rejilla. Antes eran dos columnas (`.home-feed-grid`), que no existe en el demo y
            partía la tarjeta en dos mitades estrechas. */}
        <section className="dash-card dash-card--moodboard">
          <PostItWall
            title={t('home.sent')}
            titleIcon="sent"
            /* QA (2026-08-28) — antes subía a 6 cuando "Recibidos" se ocultaba (Modo Anti-Spoiler),
               para llenar con contenido real el espacio sobrante que deja `flex:1` dentro de
               `.dash-card--moodboard` (la tarjeta iguala su alto al del hero vecino en pantallas
               anchas). Eso topaba la vista a un total de 6 y generaba una paginación rara con muy
               pocos ítems en la página 2 — el bug reportado. Ahora el tope es SIEMPRE 3, con o sin
               Recibidos visible; `align-content: start` en `.moodboard-grid` (recognition.css) ya
               evita que el sobrante DEFORME las filas — el espacio vacío que queda debajo es solo
               aire, no una rejilla estirada, y es preferible a mostrar más ítems de los pedidos. */
            pageSize={3}
            compact
            emptyIcon="✨"
            emptyText={t('home.emptySent')}
            emptyCta={{ label: t('home.cta'), onClick: scrollToForm }}
            baseParams={{ emisorId: demoUser.userId || undefined, programa: 'Fundamentales' }}
          />
          {/* Anti-Spoiler por campaña (2026-09-23) — «Recibidos» ya no se desmonta: el servidor quita
              lo protegido y `PostItWall` avisa en el muro propio. */}
          <PostItWall
            title={t('home.received')}
            titleIcon="received"
            pageSize={3}
            compact
            emptyIcon="🌟"
            emptyText={t('home.emptyReceived')}
            baseParams={{ receptorId: demoUser.userId || undefined, programa: 'Fundamentales' }}
          />
        </section>
      </div>

      {/* RF-041 (D-2) — tracker L–D. Va entre el hero y el formulario: es el sitio donde el usuario
          decide si reconoce hoy, que es justo lo que el tracker informa. Sujeto al gate de RF-043,
          así que con la racha apagada no se monta nada. */}
      <StreakWeekTracker />

      <div id="reconocer-section" className="rec-mode-bar">
        {entryPointMode === 'both' && (
          <div className="rec-mode-switch" role="tablist" aria-label={t('builder.modeLabel')}>
            <button type="button" role="tab" aria-selected={mode === 'classic'}
              className={`rec-mode-opt ${mode === 'classic' ? 'rec-mode-opt--on' : ''}`} onClick={() => setMode('classic')}>
              <i className="fas fa-list-check" /> {t('builder.modeClassic')}
            </button>
            <button type="button" role="tab" aria-selected={mode === 'interactive'}
              className={`rec-mode-opt ${mode === 'interactive' ? 'rec-mode-opt--on' : ''}`} onClick={() => setMode('interactive')}>
              <i className="fas fa-wand-magic-sparkles" /> {t('builder.modeInteractive')}
            </button>
          </div>
        )}
      </div>

      {/* Cada panel va dentro de la MISMA tarjeta que le da el demo: el formulario clásico en
          `.dash-card.dash-card--form` (`HomePage.tsx` : 558-566) y el constructor interactivo en
          `.dash-card--interactive` (: 569-576). Sin ese envoltorio el panel quedaba sin fondo,
          sin borde y sin sombra, flotando sobre el degradado. */}
      {mode === 'classic' ? (
        <div className="dash-card dash-card--form">
          <ReconocerPanel
            initialRecipients={initialRecipients}
            onSent={scrollToWall}
            canBulk={canBulk}
            onOpenBulk={() => setShowBulk(true)}
          />
        </div>
      ) : (
        <div className="dash-card--interactive">
          <WordBuilderPanel initialRecipients={initialRecipients} onSent={scrollToWall} />
        </div>
      )}

      <div ref={wallRef} />

      {showBulk && <BulkUploadModal onClose={() => setShowBulk(false)} campaignId={null} />}
    </>
  );
}

/**
 * 🔴 Reportado 7-sep-2026 — «con campaña de Brillamos activa también les muestra el reconocimiento
 * diario, y les deja reconocer desde ahí». El menú SÍ ocultaba la entrada (los tres destinos son
 * mutuamente excluyentes, ver `EXCLUSIVE_RECOGNITION_NAV_IDS`), pero la PANTALLA no se gateaba:
 * bastaba llegar a `/app/reconocer` por otra vía —un marcador, la pestaña de Teams, el enlace
 * profundo de una notificación— para tenerla completa y funcional.
 *
 * `useFixedHomeEntryGuard` (AppLayout) no lo cubría: redirige UNA sola vez al montar la app y su
 * propio comentario explica que nunca se re-evalúa a propósito, para no impedir la navegación manual
 * entre pantallas. Además decide con un margen de 4 s: si el perfil o la campaña tardan más, se queda
 * con el destino por defecto —esta pantalla— y ya no vuelve a corregirlo, mientras el menú, que no
 * tiene ese margen, acaba mostrando Brillamos. Es exactamente la pantalla del reporte.
 *
 * Por eso el gate vive aquí y con la MISMA resolución que pinta el menú: mientras rija otra entrada,
 * esta pantalla no existe.
 */
export function ReconocerView() {
  const { demoUser } = useUserContext();
  const { entryId, isLoading } = useRecognitionEntryGate(demoUser.ou);
  const destino = RECOGNITION_NAV_LINKS.find(({ id }) => id === entryId)?.to;

  // Nada se monta hasta saberlo: pintar el formulario y quitarlo después es el parpadeo que ya se
  // corrigió una vez en `useFixedHomeEntryGuard`.
  if (isLoading) return <RecognitionShell><PageSpinner /></RecognitionShell>;
  if (entryId !== 'reconocer' && destino) return <Navigate to={destino} replace />;

  return (
    <RecognitionShell>
      <ReconocerContent />
    </RecognitionShell>
  );
}

export default ReconocerView;
